import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowUp, ArrowDown, Download, Upload, DatabaseBackup, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { setAppSettingsCache } from "@/lib/appSettings";
import { DASHBOARD_BLOCKS, normalizeLayout } from "@/lib/dashboardBlocks";

const Fld = ({ label, hint, children }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">{label}</div>
    {children}
    {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
  </div>
);

const TEXT_FIELDS = [
  ["company_name", "Company name", "Shown in the footer and login page", false],
  ["company_tagline", "Company tagline", "", false],
  ["ticket_prefix", "Ticket number prefix", "Letters, numbers or dash (max 12). New tickets become e.g. PREFIX123/26; old numbers do not change.", false],
  ["login_headline_1", "Login headline - line 1", "Leave empty to use the default (translated) text", false],
  ["login_headline_2", "Login headline - line 2 (red)", "Leave empty to use the default", false],
  ["login_tagline", "Login description", "Leave empty to use the default", true],
  ["login_warning", "Login access warning", "Leave empty to use the default warning", true],
  ["email_footer", "Email footer line", "Replaces the address line at the bottom of every email. Leave empty for the default.", false],
];

export const SystemTextTab = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setForm(data)); }, []);
  if (!form) { return <div className="text-xs text-slate-500">Loading...</div>; }
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries([...TEXT_FIELDS.map(([k]) => [k, form[k] ?? ""]), ["notice_enabled", !!form.notice_enabled], ["notice_text", form.notice_text || ""], ["notice_level", form.notice_level || "info"], ["signup_domains", (form.signup_domains_text ?? (form.signup_domains || []).join(", ")).split(/[\s,;]+/).filter(Boolean)]]);
      const { data } = await api.put("/admin/settings", body);
      setForm(data);
      setAppSettingsCache(data);
      toast.success("System settings saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Check the fields - ticket prefix allows only letters, numbers and dash");
    } finally { setSaving(false); }
  };
  return (
    <div className="space-y-6 max-w-3xl" data-testid="system-text-tab">
      <div className="border border-slate-200 bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {TEXT_FIELDS.map(([k, label, hint, multi]) => (
          <div key={k} className={multi || k === "email_footer" ? "md:col-span-2" : ""}>
            <Fld label={label} hint={hint}>
              {multi
                ? <Textarea rows={3} value={form[k] || ""} onChange={set(k)} className="bg-white border-slate-200 rounded-sm text-sm" data-testid={`setting-${k}`} />
                : <Input value={form[k] || ""} onChange={set(k)} className="bg-white border-slate-200 rounded-sm h-10" data-testid={`setting-${k}`} />}
            </Fld>
          </div>
        ))}
      </div>
      <div className="border border-slate-200 bg-white p-6 space-y-4" data-testid="notice-settings">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-display text-lg font-bold">Interface notice</div>
            <div className="text-xs text-slate-500 mt-1">A note shown at the top of every page for all signed-in users (e.g. maintenance, holidays, announcements).</div>
          </div>
          <Switch checked={!!form.notice_enabled} onCheckedChange={(v) => setForm({ ...form, notice_enabled: v })} data-testid="setting-notice_enabled" className="data-[state=checked]:bg-red-600" />
        </div>
        <Textarea rows={3} value={form.notice_text || ""} onChange={set("notice_text")} placeholder="e.g. Office closed on Friday for a public holiday. Critical issues: call the on-call number." className="bg-white border-slate-200 rounded-sm text-sm" data-testid="setting-notice_text" />
        <Select value={form.notice_level || "info"} onValueChange={(v) => setForm({ ...form, notice_level: v })}>
          <SelectTrigger className="w-48 bg-white border-slate-200 rounded-sm" data-testid="setting-notice_level"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="info">Info (black)</SelectItem>
            <SelectItem value="warning">Warning (amber)</SelectItem>
            <SelectItem value="critical">Critical (red)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="border border-slate-200 bg-white p-6 space-y-3" data-testid="signup-settings">
        <div className="font-display text-lg font-bold">Customer self sign-up</div>
        <div className="text-xs text-slate-500">People whose email ends with one of these domains can sign in with Google without being added first. They join as End User (customer). Public providers like gmail.com are not allowed.</div>
        <Input value={form.signup_domains_text ?? (form.signup_domains || []).join(", ")} onChange={(e) => setForm({ ...form, signup_domains_text: e.target.value })} placeholder="kinos.tl, clientcompany.tl" className="bg-white border-slate-200 rounded-sm h-10" data-testid="setting-signup_domains" />
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-500 rounded-sm h-11 px-6" data-testid="system-settings-save-btn">{saving ? "Saving..." : "Save settings"}</Button>
      </div>
    </div>
  );
};

export const DashboardLayoutTab = () => {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [layout, setLayout] = useState(normalizeLayout(null));
  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)); }, []);
  const pick = (id) => {
    setUid(id);
    setLayout(normalizeLayout(users.find((u) => u.user_id === id)?.dashboard_layout));
  };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= layout.length) { return; }
    const next = [...layout];
    [next[i], next[j]] = [next[j], next[i]];
    setLayout(next);
  };
  const toggle = (i, v) => setLayout(layout.map((b, k) => (k === i ? { ...b, visible: v } : b)));
  const updateLocal = (dl) => setUsers(users.map((u) => (u.user_id === uid ? { ...u, dashboard_layout: dl } : u)));
  const save = async () => {
    await api.put(`/admin/users/${uid}/dashboard-layout`, { layout });
    updateLocal(layout);
    toast.success("Dashboard layout saved - the user sees it on next page load");
  };
  const reset = async () => {
    await api.delete(`/admin/users/${uid}/dashboard-layout`);
    updateLocal(null);
    setLayout(normalizeLayout(null));
    toast.success("Dashboard reset to default");
  };
  const label = (id) => DASHBOARD_BLOCKS.find((b) => b.id === id)?.label || id;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="dashboard-layout-tab">
      <div className="text-sm text-slate-600">Choose a user, then show, hide and reorder the cards and charts on their dashboard.</div>
      <Select value={uid} onValueChange={pick}>
        <SelectTrigger className="w-full sm:w-96 bg-white border-slate-200 rounded-sm" data-testid="layout-user-select"><SelectValue placeholder="Select a user" /></SelectTrigger>
        <SelectContent>
          {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {u.role.replace("_", " ")}</SelectItem>)}
        </SelectContent>
      </Select>
      {uid && (
        <div className="border border-slate-200 bg-white divide-y divide-slate-100">
          {layout.map((b, i) => (
            <div key={b.id} className="flex items-center gap-3 px-4 py-3" data-testid={`layout-row-${b.id}`}>
              <span className="font-mono text-[11px] text-slate-400 w-5">{i + 1}</span>
              <span className={`flex-1 text-sm ${b.visible ? "text-slate-900" : "text-slate-400 line-through"}`}>{label(b.id)}</span>
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 border border-slate-200 rounded-sm disabled:opacity-30 hover:border-red-400" data-testid={`layout-up-${b.id}`} aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => move(i, 1)} disabled={i === layout.length - 1} className="p-1.5 border border-slate-200 rounded-sm disabled:opacity-30 hover:border-red-400" data-testid={`layout-down-${b.id}`} aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
              <Switch checked={b.visible} onCheckedChange={(v) => toggle(i, v)} data-testid={`layout-toggle-${b.id}`} className="data-[state=checked]:bg-red-600" />
            </div>
          ))}
        </div>
      )}
      {uid && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={reset} className="rounded-sm border-slate-200" data-testid="layout-reset-btn"><RotateCcw className="h-4 w-4" /> Reset to default</Button>
          <Button onClick={save} className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="layout-save-btn">Save layout</Button>
        </div>
      )}
    </div>
  );
};

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const RestoreUpload = ({ onDone }) => {
  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirm", confirm);
      const { data } = await api.post("/admin/backup/restore", fd);
      toast.success(`Restored ${Object.keys(data.restored).length} collections. A safety backup was saved first.`);
      setFile(null); setConfirm("");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-red-300 bg-red-50/50 p-6 space-y-3" data-testid="restore-upload-panel">
      <div className="font-display text-lg font-bold text-red-700">Restore from a backup file</div>
      <div className="text-xs text-slate-600">This REPLACES all current tickets, users, knowledge base and settings with the file's contents. A safety backup is stored automatically before restoring.</div>
      <input type="file" accept=".json,.gz,application/json" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block text-sm" data-testid="restore-file-input" />
      <div className="flex flex-wrap items-center gap-2">
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type RESTORE to confirm" className="w-56 bg-white border-slate-200 rounded-sm h-10" data-testid="restore-confirm-input" />
        <Button onClick={run} disabled={!file || confirm !== "RESTORE" || busy} className="bg-red-600 hover:bg-red-500 rounded-sm h-10" data-testid="restore-upload-btn"><Upload className="h-4 w-4" /> {busy ? "Restoring..." : "Restore"}</Button>
      </div>
    </div>
  );
};

export const BackupTab = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/backups").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const backupNow = async () => {
    setBusy(true);
    try { await api.post("/admin/backups"); toast.success("Backup stored"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Backup failed"); }
    finally { setBusy(false); }
  };
  const restore = async (b) => {
    const typed = window.prompt(`Restore backup from ${new Date(b.created_at).toLocaleString()}?\nThis replaces ALL current data. Type RESTORE to confirm.`);
    if (typed !== "RESTORE") { return; }
    try {
      await api.post(`/admin/backups/${b.backup_id}/restore`, { confirm: "RESTORE" });
      toast.success("Backup restored. A safety backup was saved first.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };
  const del = async (b) => {
    if (!window.confirm("Delete this stored backup?")) { return; }
    await api.delete(`/admin/backups/${b.backup_id}`);
    load();
  };
  return (
    <div className="space-y-6 max-w-4xl" data-testid="backup-tab">
      <div className="border border-slate-200 bg-white p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-display text-lg font-bold">Full backup</div>
          <div className="text-xs text-slate-500 mt-1">Tickets, comments, users, customers, assets, knowledge base, categories, replies and settings. An automatic backup runs every day at 02:00 Dili time and is kept for 7 days.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`${API_BASE}/admin/backup/download`} data-testid="backup-download-link">
            <Button variant="outline" className="rounded-sm border-slate-200"><Download className="h-4 w-4" /> Download backup file</Button>
          </a>
          <Button onClick={backupNow} disabled={busy} className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="backup-now-btn"><DatabaseBackup className="h-4 w-4" /> {busy ? "Backing up..." : "Back up now"}</Button>
        </div>
      </div>
      <div className="border border-slate-200 bg-white">
        <div className="px-4 py-3 border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Stored backups</div>
        {items.length === 0 && <div className="p-6 text-sm text-slate-500" data-testid="backups-empty">No stored backups yet.</div>}
        {items.map((b) => (
          <div key={b.backup_id} className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`backup-row-${b.backup_id}`}>
            <span className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 border rounded-sm ${b.kind === "auto" ? "border-slate-300 text-slate-600" : b.kind === "manual" ? "border-red-300 text-red-700" : "border-amber-300 text-amber-700"}`}>{b.kind}</span>
            <span className="text-sm flex-1 min-w-[180px]">{new Date(b.created_at).toLocaleString()} <span className="text-slate-500 text-xs">· {fmtSize(b.size_bytes)} · by {b.created_by}</span></span>
            <a href={`${API_BASE}/admin/backups/${b.backup_id}/download`} className="text-xs text-slate-700 hover:text-red-600 inline-flex items-center gap-1" data-testid={`backup-download-${b.backup_id}`}><Download className="h-3.5 w-3.5" /> Download</a>
            <button onClick={() => restore(b)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`backup-restore-${b.backup_id}`}><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
            <button onClick={() => del(b)} className="text-slate-400 hover:text-red-600" data-testid={`backup-delete-${b.backup_id}`} aria-label="Delete backup"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
      <RestoreUpload onDone={load} />
    </div>
  );
};
